import React, { useEffect, useState, useCallback, Suspense } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { apiClient } from "services/Api";
import {
  CCard,
  CCardFooter,
  CFormInput,
  CCardBody,
  CDropdown,
  CDropdownToggle,
  CButton,
  CRow,
  CCol,
  CFormSelect,
  CDropdownMenu,
  CSpinner,
} from "@coreui/react";
import {
  cilChevronLeft,
  cilChevronRight,
  cilArrowLeft,
  cilFolderOpen,
  cilCloudDownload,
  cilSave,
} from "@coreui/icons";
import CIcon from "@coreui/icons-react";

import CompanyLogo from "components/CompanyLogo";
import { triggerAlert } from "components/globals/AlertNotification";
import ToastNotification from "components/globals/ToastNotification";
import LoadingScreen from "components/globals/LoadingScreen";
import SubmitApplication from "../_components/SubmitApplication";
import {
  requestApplicationPdfExport,
  triggerDownloadFromUrl,
  waitForApplicationPdfExport,
} from "../_utils/applicationPdfExport";
import { reloadWithSectionQueryParam } from "../_utils/queryParams";

import {
  sections as sections2025_16,
  importFiles as files2025_16,
} from "./mpr_2025_16/_index";

import ExcelUploadButton from "../_components/ExcelUploadButton";

import { deepEqualByContent } from "../_hooks/useLocalDrafts";
import DraftRecovery from "../_components/DraftRecovery";
import { deepClone } from "./utils";

const InterApplication = () => {
  const { id } = useParams();

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [application, setApplication] = useState(null);
  const [formData, setFormData] = useState(null);
  const [jsonData, setJsonData] = useState(null);
  const [sections, setSections] = useState(null);
  const [importFiles, setImportFiles] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const exportPollRef = React.useRef(new Set());

  const [currentSectionId, setCurrentSectionId] = useState();
  const [toast, setToast] = useState();

  const draftRef = React.useRef(false);

  const loadApplication = useCallback(async () => {
    const response = await apiClient.get(`/application/inter/${id}/`);
    const app = response.data.application;
    const content = deepClone(app.content);

    setApplication({ ...app });
    setFormData(content);
    setJsonData(response.data.jsonData);
  }, [id]);

  useEffect(() => {
    loadApplication().catch((error) =>
      console.error("Erro ao carregar candidatura:", error),
    );
  }, [loadApplication]);

  useEffect(() => {
    const exportTaskId = application?.export_task_id;
    if (!exportTaskId || exportPollRef.current.has(exportTaskId)) return;

    exportPollRef.current.add(exportTaskId);
    setLoadingPdf(true);

    waitForApplicationPdfExport({
      typology: "inter",
      applicationId: id,
      taskId: exportTaskId,
    })
      .then(async (result) => {
        await triggerDownloadFromUrl(result.downloadUrl, result.filename);
        setToast({
          title: "Pdf",
          message: `PDF ${application?.client_name || "Candidatura"} gerado com sucesso!`,
          type: "success",
        });
      })
      .catch(() =>
        setToast({
          title: "Pdf",
          message: `Ocorreu um erro ao gerar o PDF ${application?.client_name || "Candidatura"}!`,
          type: "error",
        }),
      )
      .finally(async () => {
        exportPollRef.current.delete(exportTaskId);
        setLoadingPdf(false);
        setApplication((current) =>
          current ? { ...current, export_task_id: null } : current,
        );
      });
  }, [application?.client_name, application?.export_task_id, id, loadApplication]);

  useEffect(() => {
    if (application?.notice) {
      if (application.notice === "MPr-2025-16") {
        setSections(sections2025_16);
        setImportFiles(files2025_16);
      }
    }
  }, [application]);

  useEffect(() => {
    if (sections) {
      setCurrentSectionId(queryParams.get("seccao") ?? sections[0].id);
    }
  }, [sections]);

  useEffect(() => {
    if (!queryParams.toString()) {
      window.history.pushState(
        { url: window.location.href },
        document.title,
        window.location.href
      );
    }

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const handleBeforeUnload = useCallback((event) => {
    event.preventDefault();
    event.returnValue = "";
  }, []);

  const handlePopState = useCallback((event) => {
    event.preventDefault();
    const confirmLeave = window.confirm(
      "Tens alterações que não guardaste. Queres sair na mesma?"
    );
    if (confirmLeave) {
      navigate(-1);
    } else {
      window.history.pushState(null, document.title, window.location.href);
    }
  }, []);

  const handleChange = (fieldId, value) => {
    setFormData((prevFormData) => {
      const newFormData = { ...prevFormData };
      const keys = Array.isArray(fieldId) ? fieldId : [fieldId];
      let current = newFormData;
      keys.forEach((key, index) => {
        if (index === keys.length - 1) {
          current[key] = value;
        } else {
          current = current[key] = { ...current[key] };
        }
      });
      return newFormData;
    });
  };

  const hydratedRef = React.useRef(false);
  useEffect(() => {
    if (!application || !formData) return;
    if (!hydratedRef.current) {
      hydratedRef.current = true;
      return;
    }
    draftRef.current?.scheduleCurrSave?.(1000);
  }, [formData, application]);

  const handleSave = () => {
    const base = application.content;
    const changedFields = Object.keys(formData).reduce((acc, key) => {
      if (!deepEqualByContent(formData[key], base[key])) {
        acc[key] = formData[key];
      }
      return acc;
    }, {});

    apiClient
      .put(`/application/inter/${id}/`, { content: changedFields })
      .then(() => {
        window.removeEventListener("beforeunload", handleBeforeUnload);

        triggerAlert("Candidatura guardada com sucesso!", "success");
        reloadWithSectionQueryParam(currentSectionId);

        draftRef.current?.clearAllDrafts?.();
      })
      .catch((error) =>
        setToast({ title: "Candidatura", message: error, type: "error" })
      );
  };

  const handleNext = () => {
    const currentIndex = sections.findIndex((s) => s.id === currentSectionId);
    if (currentIndex < sections.length - 1) {
      setCurrentSectionId(sections[currentIndex + 1].id);
    }
  };

  const handlePrevious = () => {
    const currentIndex = sections.findIndex((s) => s.id === currentSectionId);
    if (currentIndex > 0) {
      setCurrentSectionId(sections[currentIndex - 1].id);
    }
  };

  const downloadPDF = async () => {
    if (loadingPdf || !id) return;
    const clientName = application?.client_name || "Candidatura";

    setLoadingPdf(true);
    try {
      const startResult = await requestApplicationPdfExport({
        typology: "inter",
        applicationId: id,
      });

      if (startResult.status === "SUCCESS" && startResult.downloadUrl) {
        await triggerDownloadFromUrl(startResult.downloadUrl, startResult.filename);
        setToast({
          title: "Pdf",
          message: `PDF ${clientName} gerado com sucesso!`,
          type: "success",
        });
        setApplication((current) =>
          current ? { ...current, export_task_id: null } : current,
        );
        return;
      }

      if (startResult.status === "PENDING" && startResult.taskId) {
        const result = await waitForApplicationPdfExport({
          typology: "inter",
          applicationId: id,
          taskId: startResult.taskId,
        });
        await triggerDownloadFromUrl(result.downloadUrl, result.filename);
        setToast({
          title: "Pdf",
          message: `PDF ${clientName} gerado com sucesso!`,
          type: "success",
        });
        setApplication((current) =>
          current ? { ...current, export_task_id: null } : current,
        );
      }
    } catch (error) {
      setToast({
        title: "Pdf",
        message: `Ocorreu um erro ao gerar o PDF ${clientName}!`,
        type: "error",
      });
    } finally {
      setLoadingPdf(false);
    }
  };

  if (!application || !sections || !currentSectionId)
    return <LoadingScreen message="A carregar candidatura..." />;

  const currentSection = sections.find((s) => s.id === currentSectionId);
  const CurrentComponent = currentSection.component;

  return (
    <div id="application">
      {/* Toolbar */}
      <CCard
        className="d-flex flex-row justify-content-between align-items-center px-3 py-3 mb-3"
        style={{ position: "sticky", top: 65, zIndex: 1050 }}
      >
        <div className="d-flex gap-2">
          <CButton
            color="secondary"
            className="d-flex align-items-center"
            onClick={() => navigate("/candidaturas/internacionalizacao")}
          >
            <CIcon icon={cilArrowLeft} className="me-2" size="lg" />
            Voltar
          </CButton>

          <CDropdown>
            <CDropdownToggle className="d-flex align-items-center" color="info">
              <CIcon icon={cilFolderOpen} className="me-2" size="lg" />
              Carregar Dados
            </CDropdownToggle>
            <CDropdownMenu>
              {importFiles &&
                Object.entries(importFiles)?.map(([label, fcn]) => (
                  <ExcelUploadButton
                    label={label}
                    onFileLoaded={(wb) =>
                      fcn(wb, formData, jsonData, handleChange, setToast)
                    }
                    onException={setToast}
                  />
                ))}
              {!importFiles && (
                <small className="px-3 text-muted">Não Disponível</small>
              )}
            </CDropdownMenu>
          </CDropdown>

          <CButton
            color="success"
            className="d-flex align-items-center"
            onClick={downloadPDF}
            disabled={loadingPdf}
          >
            {loadingPdf ? (
              <CSpinner size="sm" className="me-2" />
            ) : (
              <CIcon icon={cilCloudDownload} className="me-2" size="lg" />
            )}
            Exportar
          </CButton>
        </div>

        <div className="d-flex gap-2">
          <DraftRecovery
            ref={draftRef}
            appId={id}
            notice={application?.notice}
            schemaVersion="v1"
            baseGetter={() => application?.content ?? null}
            currentGetter={() => formData ?? null}
            setFormData={setFormData}
            setToast={setToast}
          />

          <SubmitApplication
            id={id}
            typology="inter"
            taskId={application.task_id}
            submission={application.submission}
            setToast={setToast}
            pages={sections
              .filter(
                (section) =>
                  application.notice === "MPr-2025-1" ||
                  section.title !== "Custos"
              )
              .map((section) => section.title)}
          />

          <CButton
            color="success"
            className="d-flex align-items-center"
            onClick={handleSave}
          >
            <CIcon icon={cilSave} className="me-2" size="lg" />
            Guardar
          </CButton>
        </div>
      </CCard>

      <ToastNotification toast={toast} setToast={setToast} />

      {/* Header */}
      <CCard className="mb-3">
        <CCardBody className="px-5 py-4">
          <CRow className="align-items-center">
            <CCol className="text-center mr-5t" md={6}>
              {application.client_image ? (
                <CompanyLogo
                  logo={application.client_image}
                  maxWidth={200}
                  maxHeight={160}
                />
              ) : (
                <h3 className="mb-0 fw-bold">{application.client_name}</h3>
              )}
            </CCol>
            <CCol md={3}>
              <CRow className="mb-2">
                <CCol>
                  <strong>Tipologia:</strong>
                  <CFormInput
                    value="Internacionalização"
                    type="text"
                    disabled
                    readOnly
                  />
                </CCol>
              </CRow>
              <CRow>
                <CCol>
                  <strong>Aviso:</strong>
                  <CFormInput
                    value={application.notice}
                    type="text"
                    disabled
                    readOnly
                  />
                </CCol>
              </CRow>
            </CCol>
            <CCol md={3}>
              <CRow className="mb-2">
                <CCol>
                  <strong>Criado:</strong>
                  <CFormInput
                    value={formatDate(application.created_at)}
                    type="text"
                    disabled
                    readOnly
                  />
                </CCol>
              </CRow>
              <CRow>
                <CCol>
                  <strong>Ultima Atualização:</strong>
                  <CFormInput
                    value={formatDate(application.updated_at)}
                    type="text"
                    disabled
                    readOnly
                  />
                </CCol>
              </CRow>
            </CCol>
          </CRow>
        </CCardBody>
        <CCardFooter className="px-5">
          <CRow className="justify-content-between">
            <CCol md={12} className="d-flex justify-content-between">
              <CButton
                className="d-flex align-items-center me-2"
                color="secondary"
                onClick={handlePrevious}
                disabled={currentSectionId === sections[0].id}
              >
                <CIcon icon={cilChevronLeft} className="me-2" />
                <small>Anterior</small>
              </CButton>

              <CFormSelect
                value={currentSectionId}
                className="text-center fw-bold"
                onChange={(e) => setCurrentSectionId(e.target.value)}
              >
                {sections.map((section) => (
                  <option key={section.id} value={section.id}>
                    {section.title}
                  </option>
                ))}
              </CFormSelect>

              <CButton
                className="d-flex align-items-center ms-2"
                color="primary"
                onClick={handleNext}
                disabled={currentSectionId === sections[sections.length - 1].id}
              >
                <small className="me-2">Seguinte</small>
                <CIcon icon={cilChevronRight} />
              </CButton>
            </CCol>
          </CRow>
        </CCardFooter>
      </CCard>

      {/* Section */}
      <CCard className="mb-3">
        <CCardBody className="px-5 py-5">
          <Suspense
            fallback={
              <div className="pt-3 text-center">
                <CSpinner
                  color="primary"
                  style={{ width: "4rem", height: "4rem" }}
                />
              </div>
            }
          >
            <CurrentComponent
              formData={formData}
              handleChange={handleChange}
              setToast={setToast}
              jsonData={jsonData}
              setFormData={setFormData}
            />
          </Suspense>
        </CCardBody>
        {/* Section Footer */}
      </CCard>
    </div>
  );
};

export default InterApplication;

function formatDate(timestamp) {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  return date.toLocaleDateString("pt-PT", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}
